import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Documents.css';

const Documents = () => {
  const { selectedTenant, authenticatedFetch, hasAllTenantAccess } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [customName, setCustomName] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/documents?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch documents');
        setDocuments([]);
      } else {
        setDocuments(Array.isArray(data.documents) ? data.documents : []);
      }
    } catch (e) {
      console.error('Error fetching documents:', e);
      setError('Network error. Please try again.');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchDocuments();
    } else {
      setDocuments([]);
      setLoading(false);
    }
  }, [selectedTenant, fetchDocuments]);

  const handleDownload = async (doc) => {
    if (!selectedTenant || !doc?.id) return;

    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/documents/${encodeURIComponent(doc.id)}/download?tenant=${encodeURIComponent(selectedTenant.name)}`
      );

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.error('Failed to download document:', text);
        alert('Failed to download document.');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading document:', e);
      alert('Error downloading document.');
    }
  };

  const handleFileChange = (e) => {
    setUploadError(null);
    setUploadSuccess(false);
    const file = e.target.files && e.target.files[0];
    
    if (file) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setUploadError(`File is too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB`);
        setSelectedFile(null);
        e.target.value = ''; // Clear the input
        return;
      }
    }
    
    setSelectedFile(file || null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedTenant) {
      setUploadError('No tenant selected');
      return;
    }
    if (!selectedFile) {
      setUploadError('Please choose a file to upload');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      setUploadSuccess(false);

      const file = selectedFile;
      const reader = new FileReader();

      reader.onload = async () => {
        try {
          const result = reader.result;
          if (!result || typeof result !== 'string') {
            setUploadError('Failed to read file data');
            setUploading(false);
            return;
          }

          // Extract base64 data (remove data URL prefix if present)
          let base64 = result;
          if (result.includes(',')) {
            base64 = result.split(',').pop() || '';
          }
          
          if (!base64 || base64.trim().length === 0) {
            setUploadError('Failed to extract file data');
            setUploading(false);
            return;
          }

          const nameToSend = (customName && customName.trim()) || file.name || 'document';

          let response;
          try {
            response = await authenticatedFetch(
              `${getApiBaseUrl()}/api/documents?tenant=${encodeURIComponent(selectedTenant.name)}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  name: nameToSend,
                  data: base64,
                  created: new Date().toISOString().slice(0, 10)
                })
              }
            );
          } catch (fetchErr) {
            console.error('Network error during upload:', fetchErr);
            setUploadError(`Network error: ${fetchErr.message || 'Failed to connect to server'}`);
            setUploading(false);
            return;
          }

          // Get response text first to handle both JSON and non-JSON responses
          let responseText;
          try {
            responseText = await response.text();
          } catch (textErr) {
            console.error('Error reading response:', textErr);
            setUploadError(`Server error: HTTP ${response.status} ${response.statusText || 'Failed to read response'}`);
            setUploading(false);
            return;
          }

          let data;
          try {
            data = JSON.parse(responseText);
          } catch (parseErr) {
            console.error('Error parsing JSON response:', parseErr);
            console.error('Response status:', response.status, response.statusText);
            console.error('Response text (first 500 chars):', responseText.substring(0, 500));
            // If it's not JSON, show the raw response or a status-based message
            if (responseText && responseText.trim().length > 0) {
              const preview = responseText.length > 200 ? responseText.substring(0, 200) + '...' : responseText;
              setUploadError(`Server error: ${preview}`);
            } else {
              setUploadError(`Server error: HTTP ${response.status} ${response.statusText || 'Unknown error'}`);
            }
            setUploading(false);
            return;
          }

          if (!response.ok || !data.success) {
            const errorMsg = data.error || data.details || `Upload failed (HTTP ${response.status})`;
            console.error('Upload failed:', errorMsg, data);
            setUploadError(errorMsg);
            setUploading(false);
            return;
          }

          setUploadSuccess(true);
          setSelectedFile(null);
          setCustomName('');
          await fetchDocuments();
        } catch (err) {
          console.error('Error uploading document:', err);
          setUploadError('Error uploading document.');
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        console.error('File reading error:', reader.error);
        setUploadError('Failed to read file.');
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Unexpected error uploading document:', err);
      setUploadError('Unexpected error uploading document.');
      setUploading(false);
    }
  };

  if (!selectedTenant) {
    return (
      <div className="documents-container">
        <div className="documents-header">
          <h2>Documents</h2>
        </div>
        <div className="documents-empty-state">
          <Icon name="list" size="large" className="documents-empty-icon" />
          <h3>No Tenant Selected</h3>
          <p>Please select a tenant from the dropdown to view documents.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="documents-container">
        <div className="documents-header">
          <h2>Documents - {selectedTenant.name}</h2>
        </div>
        <div className="documents-loading">
          <div className="loading-spinner"></div>
          <p>Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-container">
      <div className="documents-header">
        <h2>Documents - {selectedTenant.name}</h2>
        <div className="documents-actions">
          <button className="documents-refresh-button" onClick={fetchDocuments}>
            <Icon name="refresh" size="small" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="documents-error">
          <span>⚠️ {error}</span>
        </div>
      )}

      {hasAllTenantAccess && (
        <form className="documents-upload-form" onSubmit={handleUpload}>
          <h3>Upload Document</h3>
          <div className="documents-upload-row">
            <input
              type="text"
              className="documents-name-input"
              placeholder="Display name (optional)"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif"
              onChange={handleFileChange}
              className="documents-file-input"
            />
            <button
              type="submit"
              className="documents-upload-button"
              disabled={uploading || !selectedFile}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          {uploadError && <div className="documents-upload-error">{uploadError}</div>}
          {uploadSuccess && <div className="documents-upload-success">Document uploaded successfully.</div>}
        </form>
      )}

      {documents.length === 0 ? (
        <div className="documents-empty-state">
          <Icon name="list" size="large" className="documents-empty-icon" />
          <h3>No Documents Found</h3>
          <p>No documents have been uploaded for this tenant yet.</p>
        </div>
      ) : (
        <div className="documents-grid">
          <table className="documents-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                {hasAllTenantAccess && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <button
                      type="button"
                      className="documents-link-button"
                      onClick={() => handleDownload(doc)}
                    >
                      {doc.name || `Document #${doc.id}`}
                    </button>
                  </td>
                  <td>
                    {doc.created
                      ? new Date(doc.created).toLocaleDateString()
                      : '-'}
                  </td>
                  {hasAllTenantAccess && (
                    <td>
                      <button
                        type="button"
                        className="documents-delete-button"
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to delete this document?')) {
                            return;
                          }
                          try {
                            const response = await authenticatedFetch(
                              `${getApiBaseUrl()}/api/documents/${encodeURIComponent(doc.id)}?tenant=${encodeURIComponent(selectedTenant.name)}`,
                              { method: 'DELETE' }
                            );
                            const data = await response.json().catch(() => ({}));
                            if (!response.ok || !data.success) {
                              alert(data.error || 'Failed to delete document.');
                              return;
                            }
                            await fetchDocuments();
                          } catch (err) {
                            console.error('Error deleting document:', err);
                            alert('Error deleting document.');
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Documents;


